import React, { useEffect, useState } from "react";
import {
  Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogTitle,
  Divider, FormControlLabel, Grid, IconButton, InputAdornment, TextField, Tooltip, Typography,
} from "@mui/material";
import { Add, Close, Delete, Edit, Group, Search } from "@mui/icons-material";
import CustomSnackbar from "../components/Common/CustomSnackbar";
import { useSnackbar } from "../components/Common/hooks/useSnackbar";
import useAuth from "../hoc/AuthProvider/useAuth";
import { Permissions } from "../config/permissions";
import {
  getDeviceGroupList, getDeviceGroupEntity, createDeviceGroup, updateDeviceGroup,
  deleteDeviceGroup, assignUsersDeviceGroup, UserPermissionItem,
} from "../services/deviceGroupService";
import { getUserList } from "../services/userService";

interface DeviceGroupDto {
  id: number;
  name: string;
  description: string;
  userCount: number;
}

interface UserDto { userId: number; userName: string; }

interface UserPermissionForm extends UserPermissionItem {
  userName: string;
}

const emptyForm = {
  name: "",
  description: "",
};

const DeviceGroups: React.FC = () => {
  const { havePermission } = useAuth();
  const { showSuccess, showError, snackbarProps } = useSnackbar();

  const [groups, setGroups]           = useState<DeviceGroupDto[]>([]);
  const [allUsers, setAllUsers]       = useState<UserDto[]>([]);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState("");
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editId, setEditId]           = useState<number | null>(null);
  const [form, setForm]               = useState(emptyForm);
  const [nameError, setNameError]     = useState("");
  const [userPerms, setUserPerms]     = useState<UserPermissionForm[]>([]);
  const [userInput, setUserInput]     = useState<UserDto | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groupsRes, usersRes] = await Promise.all([getDeviceGroupList(), getUserList()]);
      setGroups((groupsRes.data as any).data ?? []);
      setAllUsers((usersRes.data as any).data ?? []);
    } catch (e) {
      showError(e as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setNameError("");
    setUserPerms([]);
    setUserInput(null);
    setDialogOpen(true);
  };

  const openEdit = async (group: DeviceGroupDto) => {
    setEditId(group.id);
    setForm({ name: group.name, description: group.description ?? "" });
    setNameError("");
    setUserInput(null);
    try {
      const res = await getDeviceGroupEntity(group.id);
      const entity = (res.data as any).data;
      setUserPerms(
        (entity.users ?? []).map((u: any) => ({
          userId: u.userId, userName: u.userName,
          canCreate: u.canCreate, canEdit: u.canEdit, canDelete: u.canDelete,
        }))
      );
    } catch (e) {
      setUserPerms([]);
    }
    setDialogOpen(true);
  };

  const handleClose = () => { setDialogOpen(false); setNameError(""); };

  const handleSave = async () => {
    if (!form.name.trim()) { setNameError("Grup adı boş olamaz."); return; }
    if (form.name.trim().length > 200) { setNameError("Grup adı en fazla 200 karakter olabilir."); return; }
    try {
      let groupId = editId;
      if (editId !== null) {
        await updateDeviceGroup({ id: editId, name: form.name, description: form.description });
      } else {
        const res = await createDeviceGroup({ name: form.name, description: form.description });
        groupId = (res.data as any).data?.id;
      }
      await assignUsersDeviceGroup({
        deviceGroupId: groupId!,
        users: userPerms.map(u => ({ userId: u.userId, canCreate: u.canCreate, canEdit: u.canEdit, canDelete: u.canDelete })),
      });
      showSuccess(editId !== null ? "Grup güncellendi." : "Grup oluşturuldu.");
      setDialogOpen(false);
      await fetchData();
    } catch (e) {
      showError(e as Error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await deleteDeviceGroup(id);
      if (!(res.data as any).success) {
        showError(new Error((res.data as any).message));
        return;
      }
      showSuccess("Grup silindi.");
      await fetchData();
    } catch (e) {
      showError(e as Error);
    }
  };

  const addUser = (user: UserDto | null) => {
    if (!user) return;
    if (userPerms.some(u => u.userId === user.userId)) return;
    setUserPerms(prev => [...prev, { userId: user.userId, userName: user.userName, canCreate: false, canEdit: false, canDelete: false }]);
    setUserInput(null);
  };

  const removeUser = (userId: number) => setUserPerms(prev => prev.filter(u => u.userId !== userId));

  const updatePerm = (userId: number, field: keyof UserPermissionItem, value: boolean) => {
    setUserPerms(prev => prev.map(u => u.userId === userId ? { ...u, [field]: value } : u));
  };

  const availableUsers = allUsers.filter(u => !userPerms.some(p => p.userId === u.userId));
  const filtered = groups.filter(g => g.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <div className="panel-container">
        <Box sx={{ bgcolor: "#fff", p: 2, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", mb: 2 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid size={{ xs: 12, sm: "auto" }}>
              <TextField size="small" placeholder="Grup ara" value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
            </Grid>
            {havePermission(Permissions.DeviceGroup_Create) && (
              <Grid size={{ xs: 12, sm: "auto" }}>
                <Button variant="contained" startIcon={<Add />} onClick={openCreate}
                  sx={{ bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "var(--color-primary-dark)" }, textTransform: "none", borderRadius: "6px" }}>
                  Grup Ekle
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ bgcolor: "#fff", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", borderRadius: 1 }}>
            {filtered.map((group, idx) => (
              <Box key={group.id} sx={{
                display: "flex", alignItems: "center", p: 2,
                borderBottom: idx < filtered.length - 1 ? "1px solid #f0f0f0" : "none", gap: 2,
              }}>
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={600}>{group.name}</Typography>
                  {group.description && (
                    <Typography variant="body2" color="text.secondary">{group.description}</Typography>
                  )}
                  <Box sx={{ mt: 0.5 }}>
                    <Tooltip title="Üye sayısı" arrow placement="top">
                      <Chip label={`${group.userCount} kullanıcı`} size="small"
                        icon={<Group sx={{ fontSize: "13px !important" }} />}
                        sx={{ bgcolor: "#eff8ff", color: "#1570ef", border: "1px solid #b2ddff", "& .MuiChip-icon": { color: "#1570ef" } }} />
                    </Tooltip>
                  </Box>
                </Box>
                {havePermission(Permissions.DeviceGroup_Update) && (
                  <IconButton size="small" onClick={() => openEdit(group)}><Edit fontSize="small" /></IconButton>
                )}
                {havePermission(Permissions.DeviceGroup_Delete) && (
                  <IconButton size="small" color="error" onClick={() => handleDelete(group.id)}><Delete fontSize="small" /></IconButton>
                )}
              </Box>
            ))}
            {filtered.length === 0 && (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">Kayıt bulunamadı.</Typography>
              </Box>
            )}
          </Box>
        )}
      </div>

      <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography fontWeight={600} fontSize={18}>{editId !== null ? "Grup Düzenle" : "Grup Ekle"}</Typography>
          <IconButton onClick={handleClose} size="small"><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <Divider />
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField size="small" label="Grup Adı *" value={form.name} fullWidth
            onChange={e => { setForm({ ...form, name: e.target.value }); setNameError(""); }}
            error={!!nameError} helperText={nameError} />

          <TextField size="small" label="Açıklama" value={form.description} fullWidth multiline rows={2}
            onChange={e => setForm({ ...form, description: e.target.value })} />

          <Divider><Typography fontSize={12} color="text.secondary">Kullanıcı Atamaları</Typography></Divider>

          <Autocomplete size="small" options={availableUsers} value={userInput}
            getOptionLabel={o => o.userName} isOptionEqualToValue={(o, v) => o.userId === v.userId}
            onChange={(_, v) => addUser(v)}
            renderInput={params => <TextField {...params} label="Kullanıcı ekle" placeholder="Kullanıcı seçiniz" />} />

          {userPerms.length > 0 && (
            <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto auto auto auto", px: 1.5, py: 0.5, bgcolor: "#f8f9fa" }}>
                <Typography fontSize={11} color="text.secondary" fontWeight={600}>Kullanıcı</Typography>
                <Typography fontSize={11} color="text.secondary" fontWeight={600} sx={{ textAlign: "center", px: 1 }}>Ekle</Typography>
                <Typography fontSize={11} color="text.secondary" fontWeight={600} sx={{ textAlign: "center", px: 1 }}>Düzenle</Typography>
                <Typography fontSize={11} color="text.secondary" fontWeight={600} sx={{ textAlign: "center", px: 1 }}>Sil</Typography>
                <Box sx={{ width: 32 }} />
              </Box>
              <Divider />
              {userPerms.map((u, idx) => (
                <Box key={u.userId}>
                  {idx > 0 && <Divider />}
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto auto auto auto", alignItems: "center", px: 1.5, py: 0.25 }}>
                    <Typography fontSize={13}>{u.userName}</Typography>
                    <Checkbox size="small" checked={u.canCreate} onChange={e => updatePerm(u.userId, "canCreate", e.target.checked)} />
                    <Checkbox size="small" checked={u.canEdit} onChange={e => updatePerm(u.userId, "canEdit", e.target.checked)} />
                    <Checkbox size="small" checked={u.canDelete} onChange={e => updatePerm(u.userId, "canDelete", e.target.checked)} />
                    <IconButton size="small" onClick={() => removeUser(u.userId)}><Close fontSize="small" /></IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
            <Button variant="outlined" onClick={handleClose}
              sx={{ textTransform: "none", borderRadius: "6px", borderColor: "var(--color-primary)", color: "var(--color-primary)" }}>
              İptal
            </Button>
            <Button variant="contained" onClick={handleSave}
              sx={{ textTransform: "none", borderRadius: "6px", bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "var(--color-primary-dark)" } }}>
              Kaydet
            </Button>
          </Box>
        </Box>
      </Dialog>

      <CustomSnackbar {...snackbarProps} />
    </>
  );
};

export default DeviceGroups;
